import { useEffect, useMemo, useState } from 'preact/hooks';
import type { HealthBody } from '../shared/health';
import { menuFor } from '../shared/rules/menu';
import { AppHeader } from './components/AppHeader';
import { RequestFrame } from './components/Workspace';
import { AdminPage } from './pages/Admin';
import { HomePage } from './pages/Home';
import { MyRequestsPage } from './pages/MyRequests';
import { NewRequestPage } from './pages/NewRequest';
import { RequestDetailPage } from './pages/RequestDetail';
import { ContractsPage } from './pages/Contracts';
import { PurchaseOrdersPage } from './pages/PurchaseOrders';
import { PriceHistoryPage } from './pages/PriceHistory';
import { PoApprovalPage, PoApprovalsPage } from './pages/PoApprovals';
import { PoDocumentPage, VerifyPage } from './pages/PoDocument';
import { PrepareOrderPage } from './pages/PrepareOrder';
import { QuotesPage } from './pages/Quotes';
import { RfqPage } from './pages/Rfq';
import { QueuePage } from './pages/Queue';
import { SupplierDetailPage } from './pages/SupplierDetail';
import { SuppliersPage } from './pages/Suppliers';
import { ReviewPage } from './pages/Review';
import {
  I18nContext,
  applyLang,
  initialLang,
  translate,
  type I18n,
  type Lang,
  type MessageKey,
} from './i18n';
import { signOut } from './lib/auth';
import { AppFooter, SignInPage } from './components/SignInPage';
import { useRoute } from './lib/route';
import { ConfirmDeliveryPage } from './pages/ConfirmDelivery';
import { DnDocumentPage } from './pages/DnDocument';
import { RecordInvoicePage } from './pages/RecordInvoice';
import { DashboardPage } from './pages/Dashboard';
import { PettyCashClaimFormPage, PettyCashClaimPage, PettyCashPage } from './pages/PettyCash';
import { FinanceInboxPage, PaymentsPage, VoucherPage, VoucherPrintPage } from './pages/Vouchers';
import { SIGNED_OUT, useSession } from './lib/session';

type HealthState = { kind: 'loading' } | { kind: 'ok'; body: HealthBody } | { kind: 'error' };

function useHealth(): HealthState {
  const [health, setHealth] = useState<HealthState>({ kind: 'loading' });
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/health', { signal: controller.signal, headers: { accept: 'application/json' } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setHealth({ kind: 'ok', body: (await res.json()) as HealthBody });
      })
      .catch((err: unknown) => {
        if (!(err instanceof DOMException && err.name === 'AbortError'))
          setHealth({ kind: 'error' });
      });
    return () => controller.abort();
  }, []);
  return health;
}

export function App() {
  const [lang, setLang] = useState<Lang>(initialLang);
  useEffect(() => applyLang(lang), [lang]);
  const i18n = useMemo<I18n>(
    () => ({ lang, setLang, t: (key, vars) => translate(lang, key, vars) }),
    [lang],
  );
  const { t } = i18n;

  const path = useRoute();
  const session = useSession();
  const user = session.kind === 'signedIn' ? session.user : SIGNED_OUT;
  const health = useHealth();
  const { top, account } = menuFor(user.roles, user.poApprover === true);
  const item = [...top, ...account].find((i) => i.path === path);
  const isHome = path === '/';
  const draftNo = /^\/requests\/draft\/([A-Z]{3}-PR-\d{4,}-\d{4})$/.exec(path)?.[1];
  const renewOf = /^\/requests\/new\/renew\/((?:SMC|VIB|MED)-CT-\d{4,}-\d{4})$/.exec(path)?.[1];
  const isNewRequest = path === '/requests/new' || draftNo !== undefined || renewOf !== undefined;
  const detailNo = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})$/.exec(path)?.[1];
  // A request opened in New requests from the queue (D118).
  const reviewOf = /^\/review\/([A-Z]{3}-PR-\d{4,}-\d{4})$/.exec(path)?.[1];
  const rfqFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/rfq$/.exec(path)?.[1];
  const quotesFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/quotes$/.exec(path)?.[1];
  const poFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/po$/.exec(path)?.[1];
  const deliveryFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/delivery$/.exec(path)?.[1];
  const invoiceFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/invoice$/.exec(path)?.[1];
  const paymentsFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/payments$/.exec(path)?.[1];
  const voucherFor = /^\/vouchers\/([A-Z]{3}-PV-\d{4,}-\d{4})$/.exec(path)?.[1];
  const voucherPrintFor = /^\/vouchers\/([A-Z]{3}-PV-\d{4,}-\d{4})\/print$/.exec(path)?.[1];
  const approvalFor = /^\/po-approvals\/([A-Z]{3}-PR-\d{4,}-\d{4})$/.exec(path)?.[1];
  const poDocFor = /^\/po\/([A-Z]{3}-PO-\d{4,}-\d{4})$/.exec(path)?.[1];
  const dnDocFor = /^\/dn\/([A-Z]{3}-DN-\d{4,}-\d{4})$/.exec(path)?.[1];
  const verifyFor = /^\/verify\/([A-Z]{3}-[A-Z]{2}-\d{4,}-\d{4})$/.exec(path)?.[1];
  const supplierId = /^\/suppliers\/(SUP-[2-9A-HJ-NP-Z]{8})$/.exec(path)?.[1];
  const pcClaimFor = /^\/requests\/([A-Z]{3}-PR-\d{4,}-\d{4})\/petty-cash$/.exec(path)?.[1];
  const pcMatch = /^\/petty-cash\/([A-Z]{3}-PC-\d{4,}-\d{4})(?:\?(sent|saved)=1)?$/.exec(path);
  const pcFor = pcMatch?.[1];
  const me = session.kind === 'signedIn' ? session.me : null;

  return (
    <I18nContext.Provider value={i18n}>
      <a class="skip-link" href="#main">
        {t('skip')}
      </a>
      <AppHeader
        user={user}
        path={reviewOf ? '/review' : path}
        onSignOut={session.kind === 'signedIn' ? () => void signOut() : undefined}
      />
      {session.kind !== 'signedIn' ? (
        <main id="main" class="page page--signin" tabIndex={-1}>
          <SignInPage session={session} />
        </main>
      ) : (
        <main id="main" class="page" tabIndex={-1}>
          {me && (me.department || me.vertical) && (
            <p class="page__context">{[me.department, me.vertical].filter(Boolean).join(' · ')}</p>
          )}
          <h1 class="page__title">
            {isNewRequest
              ? draftNo
                ? t('pr.titleDraft', { no: draftNo })
                : t('pr.title')
              : rfqFor
                ? t('rfq.title')
                : quotesFor
                  ? t('quote.title')
                  : poFor
                    ? t('po.title')
                    : deliveryFor
                      ? t('dl.title')
                      : invoiceFor
                        ? t('inv.title')
                        : paymentsFor
                          ? t('pv.payments')
                          : voucherFor || voucherPrintFor
                            ? t('pv.title')
                            : approvalFor
                              ? t('nav.poApprovals')
                              : poDocFor
                                ? t('pod.title')
                                : dnDocFor
                                  ? t('dnd.title')
                                  : verifyFor
                                    ? t('verify.title')
                                    : detailNo
                                      ? t('req.detailTitle')
                                      : supplierId
                                        ? t('nav.suppliers')
                                        : path === '/dashboard'
                                          ? t('dash.title')
                                          : path === '/prices'
                                            ? t('ph.title')
                                            : pcFor || pcClaimFor || path === '/petty-cash'
                                              ? t('pc.title')
                                              : isHome || !item
                                                ? t('home.greeting', {
                                                    name: user.name ?? t('account.placeholderName'),
                                                  })
                                                : t(`nav.${item.id}` as MessageKey)}
          </h1>
          <div class="stack">
            {session.kind === 'signedIn' ? (
              isHome ? (
                <HomePage roles={user.roles} />
              ) : path === '/requests' ? (
                <MyRequestsPage />
              ) : quotesFor ? (
                session.me.roles.includes('Procurement') ? (
                  <RequestFrame prNo={quotesFor} active="quotes" roles={session.me.roles}>
                    <QuotesPage key={quotesFor} prNo={quotesFor} />
                  </RequestFrame>
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : dnDocFor ? (
                <DnDocumentPage key={dnDocFor} dnNo={dnDocFor} />
              ) : poDocFor ? (
                <PoDocumentPage key={poDocFor} poNo={poDocFor} />
              ) : verifyFor ? (
                <VerifyPage key={verifyFor} docNo={verifyFor} />
              ) : approvalFor ? (
                session.me.roles.includes('CFO') ||
                session.me.roles.includes('Procurement') ||
                session.me.poApprover ? (
                  <PoApprovalPage key={approvalFor} prNo={approvalFor} />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('cfo.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/dashboard' ? (
                <DashboardPage />
              ) : path === '/petty-cash' ? (
                session.me.roles.some((r) => ['Procurement', 'Finance', 'Admin'].includes(r)) ? (
                  <PettyCashPage procurement={session.me.roles.includes('Procurement')} />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : pcClaimFor ? (
                session.me.roles.includes('Procurement') ? (
                  <PettyCashClaimFormPage key={pcClaimFor} prNo={pcClaimFor} />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : pcFor ? (
                <PettyCashClaimPage key={path} pcNo={pcFor} flash={pcMatch?.[2]} />
              ) : path === '/payments' ? (
                session.me.roles.includes('Finance') || session.me.roles.includes('Procurement') ? (
                  <FinanceInboxPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/po-approvals' ? (
                session.me.roles.includes('CFO') || session.me.poApprover ? (
                  <PoApprovalsPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('cfo.error.forbidden')}</p>
                  </section>
                )
              ) : paymentsFor ? (
                session.me.roles.includes('Procurement') ? (
                  <RequestFrame prNo={paymentsFor} active="payment" roles={session.me.roles}>
                    <PaymentsPage key={paymentsFor} prNo={paymentsFor} />
                  </RequestFrame>
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : voucherFor ? (
                <VoucherPage key={voucherFor} pvNo={voucherFor} />
              ) : voucherPrintFor ? (
                <VoucherPrintPage key={voucherPrintFor} pvNo={voucherPrintFor} />
              ) : invoiceFor ? (
                session.me.roles.includes('Procurement') ? (
                  <RequestFrame prNo={invoiceFor} active="invoice" roles={session.me.roles}>
                    <RecordInvoicePage key={invoiceFor} prNo={invoiceFor} />
                  </RequestFrame>
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : deliveryFor ? (
                <RequestFrame prNo={deliveryFor} active="delivery" roles={session.me.roles}>
                  <ConfirmDeliveryPage key={deliveryFor} prNo={deliveryFor} />
                </RequestFrame>
              ) : poFor ? (
                session.me.roles.includes('Procurement') ? (
                  <RequestFrame prNo={poFor} active="po" roles={session.me.roles}>
                    <PrepareOrderPage key={poFor} prNo={poFor} />
                  </RequestFrame>
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : rfqFor ? (
                session.me.roles.includes('Procurement') ? (
                  <RequestFrame prNo={rfqFor} active="quotes" roles={session.me.roles}>
                    <RfqPage key={rfqFor} prNo={rfqFor} />
                  </RequestFrame>
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : detailNo ? (
                <RequestDetailPage
                  key={detailNo}
                  prNo={detailNo}
                  procurement={session.me.roles.includes('Procurement')}
                  cfo={session.me.roles.includes('CFO')}
                />
              ) : isNewRequest ? (
                <NewRequestPage
                  key={draftNo ?? renewOf ?? 'new'}
                  draftNo={draftNo}
                  renewOf={renewOf}
                />
              ) : supplierId ? (
                session.me.roles.includes('Procurement') ? (
                  <SupplierDetailPage key={supplierId} supplierId={supplierId} />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('sup.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/suppliers' ? (
                session.me.roles.includes('Procurement') ? (
                  <SuppliersPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/queue' ? (
                session.me.roles.includes('Procurement') ? (
                  <QueuePage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/review' || reviewOf ? (
                session.me.roles.includes('Procurement') ? (
                  <ReviewPage key={reviewOf ?? ''} focus={reviewOf} />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/prices' ? (
                session.me.roles.includes('Procurement') || session.me.roles.includes('CFO') ? (
                  <PriceHistoryPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/purchase-orders' ? (
                session.me.roles.includes('Procurement') || session.me.roles.includes('CFO') ? (
                  <PurchaseOrdersPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/contracts' ? (
                session.me.roles.includes('Procurement') ? (
                  <ContractsPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('review.error.forbidden')}</p>
                  </section>
                )
              ) : path === '/admin' ? (
                session.me.roles.includes('Admin') ? (
                  <AdminPage />
                ) : (
                  <section class="card" role="alert">
                    <p>{t('admin.error.forbidden')}</p>
                  </section>
                )
              ) : (
                <section class="card">
                  <p>{t('page.comingSoon')}</p>
                </section>
              )
            ) : null}
            {/* Service line: for Admin only; the sign-in screen has none (Fahad, 2 Nov). */}
            {user.roles.includes('Admin') && (
              <p class="caption" aria-live="polite" data-testid="health">
                {health.kind === 'loading' && t('health.checking')}
                {health.kind === 'ok' && t('health.ok', { version: health.body.version })}
                {health.kind === 'error' && t('health.error')}
              </p>
            )}
          </div>
        </main>
      )}
      <AppFooter />
    </I18nContext.Provider>
  );
}
